import { supabase } from '../lib/supabase';

// Clock in - creates a new clock in record
export const clockIn = async (notes = '') => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Check if user is already clocked in (has an active clock in without clock out)
    const { data: activeClockIn } = await supabase
      .from('clock_in_log')
      .select('id')
      .eq('user_id', user.id)
      .is('clock_out_time', null)
      .single();

    if (activeClockIn) {
      throw new Error('You are already clocked in');
    }

    const { data, error } = await supabase
      .from('clock_in_log')
      .insert({
        user_id: user.id,
        clock_in_time: new Date().toISOString(),
        notes: notes || null,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error clocking in:', error);
    throw error;
  }
};

// Clock out - updates the most recent clock in record with clock out time
export const clockOut = async (notes = '') => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Find the active clock in (most recent without clock out)
    const { data: activeClockIn, error: findError } = await supabase
      .from('clock_in_log')
      .select('id, notes')
      .eq('user_id', user.id)
      .is('clock_out_time', null)
      .order('clock_in_time', { ascending: false })
      .limit(1)
      .single();

    if (findError || !activeClockIn) {
      throw new Error('You are not currently clocked in');
    }

    // Update with clock out time
    const updatedNotes = notes || activeClockIn.notes || null;
    const { data, error } = await supabase
      .from('clock_in_log')
      .update({
        clock_out_time: new Date().toISOString(),
        notes: updatedNotes,
      })
      .eq('id', activeClockIn.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error clocking out:', error);
    throw error;
  }
};

// Get current clock status for the user
export const getCurrentClockStatus = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { isClockedIn: false, clockInRecord: null };
    }

    const { data, error } = await supabase
      .from('clock_in_log')
      .select('*')
      .eq('user_id', user.id)
      .is('clock_out_time', null)
      .order('clock_in_time', { ascending: false })
      .limit(1)
      .single();

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return {
      isClockedIn: !!data,
      clockInRecord: data || null,
    };
  } catch (error) {
    console.error('Error getting clock status:', error);
    throw error;
  }
};

// Get clock in/out log for a specific user (or all users if admin)
export const getClockInLog = async (userId = null, limit = 100) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Check if user is admin or manager
    const isAdminOrManager = user?.user_metadata?.role === 'admin' || user?.user_metadata?.role === 'manager';

    let query = supabase
      .from('clock_in_log')
      .select(`
        *,
        users:user_id (
          id,
          email,
          raw_user_meta_data
        )
      `)
      .order('clock_in_time', { ascending: false })
      .limit(limit);

    // If not admin/manager, only show their own records
    // If admin/manager and userId is provided, show that user's records
    // If admin/manager and userId is null, show all records
    if (!isAdminOrManager) {
      query = query.eq('user_id', user.id);
    } else if (userId) {
      query = query.eq('user_id', userId);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting clock in log:', error);
    throw error;
  }
};

// Get clock in/out log for all users (admin only). Optional filters: userId, dateFrom, dateTo (ISO strings).
export const getAllClockInLog = async (limit = 500, filters = {}) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Check if user is admin or manager
    const isAdminOrManager = user?.user_metadata?.role === 'admin' || user?.user_metadata?.role === 'manager';
    if (!isAdminOrManager) {
      throw new Error('Only admins and managers can view all clock in logs');
    }

    let query = supabase
      .from('clock_in_log')
      .select('*')
      .order('clock_in_time', { ascending: false })
      .limit(limit);

    if (filters.userId) {
      query = query.eq('user_id', filters.userId);
    }
    if (filters.dateFrom) {
      query = query.gte('clock_in_time', filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lt('clock_in_time', filters.dateTo);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Fetch user information separately from public.users table
    const userIds = [...new Set((data || []).map(record => record.user_id))];
    const userMap = {};

    if (userIds.length > 0) {
      const { data: usersData } = await supabase
        .from('users')
        .select('id, email, full_name, role')
        .in('id', userIds);

      if (usersData) {
        usersData.forEach(u => {
          userMap[u.id] = {
            id: u.id,
            email: u.email,
            raw_user_meta_data: {
              full_name: u.full_name || '',
              role: u.role || 'user'
            }
          };
        });
      }
    }

    // Attach user info to each record
    return (data || []).map(record => ({
      ...record,
      users: userMap[record.user_id] || { id: record.user_id, email: 'Unknown User', raw_user_meta_data: {} }
    }));
  } catch (error) {
    console.error('Error getting all clock in log:', error);
    throw error;
  }
};
